import {
  baseName,
  buildLayerTree,
  type LayerNode,
} from '@/designs-dev/inspect/fiber';

// A comment hangs off a component on the screen. Its anchor names the
// component and which one of that name it is, counting in reading order:
// `Button#2` is the third Button. Built from the same layer tree as drill
// mode, so a pin lands on what the layers panel would call that thing. A click
// that isn't on a component anchors to the screen itself.
//
// Counting by name keeps an anchor stable while a screen is restyled, but not
// while components are added above it: the count shifts and the pin moves
// along. Good enough for a prototype that is being looked at, not rebuilt.
export const SCREEN_ANCHOR = 'screen';

const flatten = (nodes: LayerNode[], out: LayerNode[] = []): LayerNode[] => {
  for (const n of nodes) {
    out.push(n);
    flatten(n.children, out);
  }
  return out;
};

export const anchorFor = (
  screenEl: HTMLElement,
  target: HTMLElement,
): { anchor: string; el: HTMLElement } => {
  const layers = flatten(buildLayerTree(screenEl));
  // The click lands on a DOM node; walk up to the nearest one a layer owns.
  for (
    let n: HTMLElement | null = target;
    n && n !== screenEl;
    n = n.parentElement
  ) {
    const layer = layers.find((l) => l.inspection.domNode === n);
    if (!layer) continue;
    const name = baseName(layer.inspection.name);
    const index = layers
      .filter((l) => baseName(l.inspection.name) === name)
      .indexOf(layer);
    return { anchor: `${name}#${index}`, el: n };
  }
  return { anchor: SCREEN_ANCHOR, el: screenEl };
};

// Resolves many anchors against one walk of the tree. A missing entry means
// that component isn't on the screen now (another scene, or it was removed).
export const resolveAnchors = (
  screenEl: HTMLElement,
  anchors: string[],
): Map<string, HTMLElement> => {
  const out = new Map<string, HTMLElement>();
  const byName = new Map<string, HTMLElement[]>();
  for (const l of flatten(buildLayerTree(screenEl))) {
    const el = l.inspection.domNode;
    if (!el) continue;
    const name = baseName(l.inspection.name);
    byName.set(name, [...(byName.get(name) ?? []), el]);
  }
  for (const anchor of anchors) {
    if (anchor === SCREEN_ANCHOR) {
      out.set(anchor, screenEl);
      continue;
    }
    const [name, index] = anchor.split('#');
    const el = byName.get(name)?.[Number(index)];
    if (el) out.set(anchor, el);
  }
  return out;
};
